// client/src/pages/pmPortal/TenantPayPage.js — Resident Portal → Pay Rent.
//
// The tenant picks an amount (the open balance by default) and a method, sees the
// exact total — including any card convenience fee, which is shown as a separate line
// and is NOT rent — and only then is sent to Stripe's own payment page. Nothing is
// typed into this site: bank and card details go to Stripe.
//
// A bank (ACH) payment takes a few business days to clear. It appears on the ledger
// only once the money has actually arrived; until then it shows here as "processing".
// Autopay: the tenant links a bank account once (Stripe's set-up page, with the
// authorization text) and the balance due is drawn on the rent due day.
import React, { useCallback, useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Stat, Table, Loading, Notice, Pill, Button, Input, money, fmtDate, titleCase, statusTone }
    from '../../components/secure/pm/PMKit';

const RESULT_NOTICES = {
    success: { tone: 'good', title: 'Thank you — your payment was submitted.',
        message: 'Card payments post within minutes. Bank payments take 3–5 business days to clear and appear on your ledger once the money arrives.' },
    autopay: { tone: 'good', title: 'Your bank account is being linked for autopay.',
        message: 'If your bank needed small test deposits, Stripe will e-mail you to confirm them; autopay starts once the account is verified.' },
    cancelled: { tone: 'info', title: 'Nothing was charged.', message: 'You left the payment page before paying.' },
};

const PAY_TONE = { succeeded: 'good', processing: 'warn', created: 'slate', failed: 'bad', returned: 'bad', canceled: 'slate', posting_failed: 'warn' };
const PAY_LABEL = { succeeded: 'Received', processing: 'Processing', created: 'Started', failed: 'Failed',
    returned: 'Returned by bank', canceled: 'Cancelled', posting_failed: 'Received — being recorded' };

const TenantPayPage = () => {
    const api = usePmPortalApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState(() => {
        const r = new URLSearchParams(window.location.search).get('result');
        return RESULT_NOTICES[r] || null;
    });
    const [leaseId, setLeaseId] = useState(null);
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('ach');
    const [quote, setQuote] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api.get('/api/pm-portal/tenant/pay').then((d) => {
            setData(d);
            const first = (d.leases || [])[0];
            if (first) {
                setLeaseId((cur) => cur || first.lease_id);
                setAmount((cur) => (cur === '' ? (first.payable > 0 ? first.payable.toFixed(2) : '') : cur));
            }
        }).catch((e) => setError(e.message));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    if (error) return <Notice tone="bad" title="Could not load payment options" message={error} />;
    if (!data) return <Loading />;

    const lease = (data.leases || []).find((l) => l.lease_id === leaseId) || null;
    const blocked = lease ? (method === 'card' ? lease.card_blocked : lease.ach_blocked) : ['No current lease.'];

    const review = async () => {
        setQuote(null);
        try {
            setQuote(await api.post('/api/pm-portal/tenant/pay/quote', { lease_id: leaseId, amount: Number(amount), method }));
        } catch (e) { setNotice({ tone: 'bad', title: 'Could not work out the amount', message: e.message }); }
    };

    const pay = async () => {
        setBusy(true);
        try {
            const out = await api.post('/api/pm-portal/tenant/pay/checkout', { lease_id: leaseId, amount: Number(amount), method });
            window.location.assign(out.url);
        } catch (e) {
            setNotice({ tone: 'bad', title: 'Payment not started', message: e.message });
            setBusy(false);
        }
    };

    const autopayOn = async () => {
        setBusy(true);
        try {
            const out = await api.post('/api/pm-portal/tenant/autopay/setup', { lease_id: leaseId });
            window.location.assign(out.url);
        } catch (e) {
            setNotice({ tone: 'bad', title: 'Autopay not started', message: e.message });
            setBusy(false);
        }
    };

    const autopayOff = async (enrollmentId) => {
        if (!window.confirm('Turn autopay off? Rent will no longer be drawn automatically.')) return;
        try {
            const out = await api.post(`/api/pm-portal/tenant/autopay/${enrollmentId}/cancel`, {});
            setNotice({ tone: 'good', title: 'Autopay is off', message: out.message });
            load();
        } catch (e) { setNotice({ tone: 'bad', title: 'Could not turn autopay off', message: e.message }); }
    };

    return (
        <>
            <PageHeader title="Pay rent"
                subtitle="Pay by bank account (no fee) or by card. You are sent to our payment processor's secure page; this site never sees your bank or card details." />
            {notice && <Notice {...notice} onDismiss={() => setNotice(null)} />}

            {!data.enabled && (
                <Notice tone="info" title="Online payment is not available yet"
                    message="Please pay the office directly. Your charges and payments are on the Charges & Payments page." />
            )}

            {data.enabled && !lease && <Notice tone="info" message="You have no current lease to pay." />}

            {data.enabled && lease && (
                <>
                    {(data.leases || []).length > 1 && (
                        <div className="mb-4 flex flex-wrap gap-2">
                            {data.leases.map((l) => (
                                <Button key={l.lease_id} variant={l.lease_id === leaseId ? 'primary' : 'ghost'}
                                    onClick={() => { setLeaseId(l.lease_id); setAmount(l.payable > 0 ? l.payable.toFixed(2) : ''); setQuote(null); }}>
                                    {l.property_name} · unit {l.unit_number}
                                </Button>
                            ))}
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <Stat label="Open balance" value={money(lease.open_balance)} tone={lease.open_balance > 0 ? 'warn' : 'good'} />
                        <Stat label="Payments still clearing" value={money(lease.in_flight)} />
                        <Stat label="You can pay now" value={money(lease.payable)} />
                    </div>

                    {lease.hold && <Notice tone="warn" title="Bank payments are on hold" message={lease.hold} />}

                    <Panel title="Make a payment">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input label="Amount" type="number" min="1" step="0.01" value={amount}
                                onChange={(e) => { setAmount(e.target.value); setQuote(null); }}
                                hint={`Up to ${money(lease.payable)}. Payments go to the oldest charge first.`} />
                            <div className="mb-3">
                                <span className="text-xs font-medium text-slate-600">Pay by</span>
                                <div className="mt-1 flex flex-col gap-2 text-sm">
                                    <label className="flex items-center gap-2">
                                        <input type="radio" name="method" checked={method === 'ach'}
                                            onChange={() => { setMethod('ach'); setQuote(null); }} />
                                        Bank account (ACH) — no fee, clears in 3–5 business days
                                    </label>
                                    {data.card_enabled && (
                                        <label className="flex items-center gap-2">
                                            <input type="radio" name="method" checked={method === 'card'}
                                                onChange={() => { setMethod('card'); setQuote(null); }} />
                                            Card{data.card_fee ? ` — convenience fee ${data.card_fee.pct}% + ${money(data.card_fee.fixed)}` : ''}
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                        {blocked.length > 0 && <Notice tone="warn" title="This payment cannot be made online" problems={blocked} />}
                        {blocked.length === 0 && !quote && <Button onClick={review} disabled={!(Number(amount) > 0)}>Review payment</Button>}
                        {quote && (
                            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-w-md">
                                <div className="flex justify-between text-sm"><span>Rent and charges</span><span>{money(quote.rent_amount)}</span></div>
                                {quote.convenience_fee > 0 && (
                                    <div className="flex justify-between text-sm mt-1">
                                        <span>Card convenience fee <span className="text-xs text-slate-500">(kept by the brokerage; not rent)</span></span>
                                        <span>{money(quote.convenience_fee)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between font-semibold border-t border-slate-200 mt-2 pt-2">
                                    <span>Total to be charged</span><span>{money(quote.total)}</span>
                                </div>
                                {quote.method === 'card' && (
                                    <p className="text-xs text-slate-500 mt-2">Prefer no fee? Choose bank account instead.</p>
                                )}
                                <div className="mt-3 flex gap-2">
                                    <Button onClick={pay} disabled={busy}>{busy ? 'Opening…' : `Pay ${money(quote.total)} securely`}</Button>
                                    <Button variant="ghost" onClick={() => setQuote(null)} disabled={busy}>Change</Button>
                                </div>
                            </div>
                        )}
                    </Panel>

                    {data.autopay_enabled && (
                        <Panel title="Autopay"
                            subtitle="Link a bank account once and the balance due is drawn on your rent due day. No fee. You can turn it off here at any time.">
                            {lease.autopay && lease.autopay.status === 'active' && (
                                <div className="flex items-center gap-4 text-sm">
                                    <span>On — {lease.autopay.bank_name || 'bank account'} ending {lease.autopay.bank_last4 || '••••'}, drawn on day {lease.rent_due_day || 1} of each month.</span>
                                    <Button variant="ghost" onClick={() => autopayOff(lease.autopay.enrollment_id)}>Turn off</Button>
                                </div>
                            )}
                            {lease.autopay && lease.autopay.status === 'pending' && (
                                <div className="flex items-center gap-4 text-sm">
                                    <span>Waiting for your bank account to be verified.</span>
                                    <Button variant="ghost" onClick={() => autopayOff(lease.autopay.enrollment_id)}>Cancel</Button>
                                </div>
                            )}
                            {lease.autopay && lease.autopay.status === 'suspended' && (
                                <Notice tone="warn" title="Autopay is suspended"
                                    message="A bank payment was returned. Please contact the office." />
                            )}
                            {!lease.autopay && (lease.autopay_blocked.length > 0
                                ? <Notice tone="info" problems={lease.autopay_blocked} />
                                : <Button onClick={autopayOn} disabled={busy}>Set up autopay</Button>)}
                        </Panel>
                    )}
                </>
            )}

            <Panel title="Your online payments">
                <Table
                    columns={[
                        { key: 'created_at', label: 'Started', nowrap: true, render: (r) => fmtDate(r.created_at) },
                        { key: 'method', label: 'Method', render: (r) => (r.method === 'ach' ? 'Bank' : 'Card') + (r.purpose === 'autopay' ? ' (autopay)' : '') },
                        { key: 'rent_amount', label: 'Rent', align: 'right', render: (r) => money(r.rent_amount) },
                        { key: 'convenience_fee', label: 'Fee', align: 'right', render: (r) => money(r.convenience_fee) },
                        { key: 'total_charged', label: 'Total', align: 'right', render: (r) => money(r.total_charged) },
                        { key: 'status', label: 'Status', render: (r) => <Pill tone={PAY_TONE[r.status] || statusTone(r.status)}>{PAY_LABEL[r.status] || titleCase(r.status)}</Pill> },
                    ]}
                    rows={data.recent || []}
                    toolbar={false}
                    empty="No online payments yet."
                />
            </Panel>

            <p className="text-xs text-slate-500 mt-2">
                Rent is received into a special (trust) account under 68 IAC 1450.750, in full. Any card convenience
                fee is the brokerage's charge for card acceptance and is not applied to rent.
            </p>
        </>
    );
};

export default TenantPayPage;
